/**
 * Dropbox API Service
 *
 * Functions for interacting with the Dropbox API after OAuth authentication
 */

import type { DropboxFile, DropboxFolder, DropboxUserInfo } from './types';
import { getValidDropboxTokens } from './auth';
import { reportError } from '@/lib/errors';

// Base URL for Dropbox API v2
const DROPBOX_API_BASE = 'https://api.dropboxapi.com/2';
const DROPBOX_CONTENT_BASE = 'https://content.dropboxapi.com/2';

/**
 * Make an authenticated request to the Dropbox API
 *
 * Note: Dropbox API uses POST for all endpoints with JSON body
 *
 * @param endpoint - API endpoint (e.g., '/files/list_folder')
 * @param body - Request body
 * @returns Parsed JSON response
 */
async function dropboxRequest<T>(
  endpoint: string,
  body: Record<string, unknown> = {}
): Promise<T> {
  const tokens = await getValidDropboxTokens();
  if (!tokens) {
    throw new Error('Not authenticated with Dropbox');
  }

  const response = await fetch(`${DROPBOX_API_BASE}${endpoint}`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${tokens.accessToken}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`Dropbox API error: ${response.status} - ${error}`);
  }

  return response.json();
}

/**
 * Response type for list_folder and list_folder/continue endpoints
 */
interface ListFolderResponse {
  entries: Array<{
    '.tag': 'file' | 'folder';
    id: string;
    name: string;
    path_lower: string;
    path_display: string;
    size?: number;
    server_modified?: string;
    client_modified?: string;
  }>;
  cursor: string;
  has_more: boolean;
}

/**
 * List contents of a Dropbox folder
 *
 * Returns files and folders separately for easier handling
 *
 * @param path - Folder path (empty string or '' for root)
 * @returns Files, folders, cursor for pagination, and hasMore flag
 */
export async function listDropboxFolder(
  path: string = ''
): Promise<{
  files: DropboxFile[];
  folders: DropboxFolder[];
  cursor: string;
  hasMore: boolean;
}> {
  const response = await dropboxRequest<ListFolderResponse>(
    '/files/list_folder',
    {
      path: path || '',
      include_mounted_folders: true,
      include_non_downloadable_files: false,
    }
  );

  const files: DropboxFile[] = [];
  const folders: DropboxFolder[] = [];

  for (const entry of response.entries) {
    if (entry['.tag'] === 'folder') {
      folders.push({
        id: entry.id,
        name: entry.name,
        pathLower: entry.path_lower,
        pathDisplay: entry.path_display,
      });
    } else {
      files.push({
        id: entry.id,
        name: entry.name,
        pathLower: entry.path_lower,
        pathDisplay: entry.path_display,
        isFolder: false,
        size: entry.size,
        serverModified: entry.server_modified,
        clientModified: entry.client_modified,
      });
    }
  }

  return {
    files,
    folders,
    cursor: response.cursor,
    hasMore: response.has_more,
  };
}

/**
 * Continue listing folder contents (pagination)
 *
 * Use this when listDropboxFolder returns hasMore: true
 *
 * @param cursor - Cursor from previous list_folder call
 * @returns Files, folders, new cursor, and hasMore flag
 */
export async function listDropboxFolderContinue(cursor: string): Promise<{
  files: DropboxFile[];
  folders: DropboxFolder[];
  cursor: string;
  hasMore: boolean;
}> {
  const response = await dropboxRequest<ListFolderResponse>(
    '/files/list_folder/continue',
    {
      cursor,
    }
  );

  const files: DropboxFile[] = [];
  const folders: DropboxFolder[] = [];

  for (const entry of response.entries) {
    if (entry['.tag'] === 'folder') {
      folders.push({
        id: entry.id,
        name: entry.name,
        pathLower: entry.path_lower,
        pathDisplay: entry.path_display,
      });
    } else {
      files.push({
        id: entry.id,
        name: entry.name,
        pathLower: entry.path_lower,
        pathDisplay: entry.path_display,
        isFolder: false,
        size: entry.size,
        serverModified: entry.server_modified,
        clientModified: entry.client_modified,
      });
    }
  }

  return {
    files,
    folders,
    cursor: response.cursor,
    hasMore: response.has_more,
  };
}

/**
 * Get folder metadata
 *
 * Used for breadcrumb navigation and folder info display
 *
 * @param path - Folder path
 * @returns Folder info or null if not found
 */
export async function getDropboxFolderInfo(
  path: string
): Promise<DropboxFolder | null> {
  // Handle root folder specially
  if (!path || path === '') {
    return { id: 'root', name: 'Dropbox', pathLower: '', pathDisplay: '' };
  }

  try {
    const response = await dropboxRequest<{
      id: string;
      name: string;
      path_lower: string;
      path_display: string;
    }>('/files/get_metadata', {
      path,
    });

    return {
      id: response.id,
      name: response.name,
      pathLower: response.path_lower,
      pathDisplay: response.path_display,
    };
  } catch {
    return null;
  }
}

/**
 * Get current user's Dropbox account info
 *
 * Used to display connected account in the UI
 *
 * @returns User info or null if not authenticated
 */
export async function getDropboxUserInfo(): Promise<DropboxUserInfo | null> {
  const tokens = await getValidDropboxTokens();
  if (!tokens) return null;

  try {
    const response = await fetch(
      `${DROPBOX_API_BASE}/users/get_current_account`,
      {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokens.accessToken}`,
        },
      }
    );

    if (!response.ok) return null;

    const data = await response.json();

    return {
      accountId: data.account_id,
      email: data.email,
      name: data.name?.display_name || data.name?.given_name || 'Unknown',
      profilePhotoUrl: data.profile_photo_url,
    };
  } catch {
    return null;
  }
}

/**
 * Check if the user is connected to Dropbox
 *
 * Verifies tokens are valid and can make API requests
 *
 * @returns true if connected and authenticated
 */
export async function checkDropboxConnection(): Promise<boolean> {
  const tokens = await getValidDropboxTokens();
  if (!tokens) return false;

  try {
    const userInfo = await getDropboxUserInfo();
    return userInfo !== null;
  } catch {
    return false;
  }
}

/**
 * Get a shareable link for a file or folder
 *
 * First checks for existing shared links, then creates one if needed
 *
 * @param path - File or folder path
 * @returns Shareable URL or null if failed
 */
export async function getDropboxShareLink(path: string): Promise<string | null> {
  try {
    // First try to get existing shared link
    const existingResponse = await dropboxRequest<{
      links: Array<{ url: string }>;
    }>('/sharing/list_shared_links', {
      path,
      direct_only: true,
    });

    if (existingResponse.links.length > 0) {
      return existingResponse.links[0].url;
    }

    // Create new shared link
    const newResponse = await dropboxRequest<{ url: string }>(
      '/sharing/create_shared_link_with_settings',
      {
        path,
        settings: {
          requested_visibility: 'public',
        },
      }
    );

    return newResponse.url;
  } catch {
    return null;
  }
}

/**
 * Download file content as ArrayBuffer
 *
 * Used for indexing data room documents for RAG retrieval
 *
 * Note: Uses the content endpoint, not the API endpoint
 *
 * @param path - Dropbox file path
 * @returns File content as ArrayBuffer, or null if download failed
 */
export async function downloadFileContent(
  path: string
): Promise<ArrayBuffer | null> {
  const tokens = await getValidDropboxTokens();
  if (!tokens) {
    throw new Error('Not authenticated with Dropbox');
  }

  try {
    // Dropbox download uses the content endpoint with path in header
    const response = await fetch(`${DROPBOX_CONTENT_BASE}/files/download`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens.accessToken}`,
        'Dropbox-API-Arg': JSON.stringify({ path }),
      },
    });

    if (!response.ok) {
      const error = await response.text();
      reportError(new Error(`Dropbox download error: ${response.status} ${error}`), { context: 'DropboxApi.downloadFile', level: 'warning' });
      return null;
    }

    return response.arrayBuffer();
  } catch (error) {
    reportError(error, { context: 'DropboxApi.downloadFile', level: 'warning' });
    return null;
  }
}

/**
 * Download file content as base64 string
 *
 * Convenience wrapper for indexing documents
 *
 * @param path - Dropbox file path
 * @returns Base64 encoded file content, or null if download failed
 */
export async function downloadFileAsBase64(
  path: string
): Promise<string | null> {
  const content = await downloadFileContent(path);
  if (!content) return null;

  // Convert ArrayBuffer to base64
  const bytes = new Uint8Array(content);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}
